const pluginName = 'estimatedFinishTimePlugin';
const defaults = {
  callbacks: {
    initialized: () => {},
    setup: () => {},
  },
};
const classes = {
  hidden: 'd-none hidden',
  conversionPace: 'estimated-finish-time__conversion--pace',
  conversionTime: 'estimated-finish-time__conversion--time',
  emptyReadout: 'estimated-finish-time__group--empty-readout',
};
const selectors = {
  conversion: '[data-js-estimated-finish-time-conversion]',
  duration: '[data-js-estimated-finish-time-duration]',
  durationCalculated: '[data-js-estimated-finish-time-duration-calculated]',
  durationFeedback: '[data-js-estimated-finish-time-duration-feedback]',
  durationReadout: '[data-js-estimated-finish-time-duration-readout]',
  durationUnit: '[data-js-estimated-finish-time-unit]',
  pace: '[data-js-estimated-finish-time-pace]',
  paceCalculated: '[data-js-estimated-finish-time-pace-calculated]',
  paceFeedback: '[data-js-estimated-finish-time-pace-feedback]',
  paceMinutes: '[data-js-estimated-finish-time-pace-minutes]',
  entryMode: '[data-js-estimated-finish-time-entry-mode]',
  paceReadout: '[data-js-estimated-finish-time-pace-readout]',
  paceSeconds: '[data-js-estimated-finish-time-pace-seconds]',
  paceUnit: '[data-js-estimated-finish-time-pace-unit]',
  resultUrl: '[data-js-estimated-finish-time-result-url]',
  resultUrlInput: '[data-js-estimated-finish-time-result-url-input]',
};
const METRES_PER_KILOMETRE = 1000;
const METRES_PER_MILE = 1609.344;
// 23:59:59. Keep this aligned with EstimatedFinishTimeSubmissionValidator::MAX_FINISH_TIME_SECONDS.
const MAX_FINISH_TIME_SECONDS = (23 * 3600) + (59 * 60) + 59;

/**
 * Calls the provided custom callback and triggers the provided custom plugin event (if they exist)
 * @param data
 * @param hookName
 * @param parameters
 */
const triggerHook = (data, hookName, ...parameters) => {
  data.elements.$element
    .trigger(`${pluginName}.${hookName}`, [data.elements.$element, data, ...parameters]);
  data.settings.callbacks[hookName]
    .call(data.elements.$element, data.elements.$element, data, ...parameters);
};

/**
 * Toggle the visibility of the plugin
 * @param data
 * @param isVisible
 */
const toggleVisibility = (data, isVisible) => {
  data.elements.$element.toggleClass(classes.hidden, !isVisible);
};

/**
 * Serialize the duration and optional result URL from the injected form
 * @param data
 * @returns {string}
 */
const serialize = (data) => {
  const formData = data.elements.$element.serializeArray();
  return $.param(formData);
};

/**
 * Clear the plugin's contents
 */
const clear = (data) => {
  data.elements.$element.empty();
  data.setup = false;
};

/**
 * @param value
 * @returns {number}
 */
const parseWholeNumber = (value) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/**
 * @param unit
 * @returns {number}
 */
const metresPerUnit = (unit) => (unit === 'mi' ? METRES_PER_MILE : METRES_PER_KILOMETRE);

/**
 * @param $fields
 * @returns {number}
 */
const finishTimeSecondsFromFields = ($fields) => {
  const [hours, minutes, seconds] = $fields.toArray().map((field) => parseWholeNumber($(field).val()));
  return (hours * 3600) + (minutes * 60) + seconds;
};

/**
 * @param $minutes
 * @param $seconds
 * @returns {number}
 */
const paceSecondsFromFields = ($minutes, $seconds) => (
  (parseWholeNumber($minutes.val()) * 60) + parseWholeNumber($seconds.val())
);

/**
 * @param totalSeconds
 * @param $fields
 */
const writeFinishTimeFields = (totalSeconds, $fields) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const values = [hours, minutes, seconds];

  $fields.each((index, field) => {
    $(field).val(values[index] === 0 && index === 0 ? '' : values[index]);
  });
};

/**
 * @param totalSeconds
 * @param $minutes
 * @param $seconds
 */
const writePaceFields = (totalSeconds, $minutes, $seconds) => {
  $minutes.val(Math.floor(totalSeconds / 60));
  $seconds.val(totalSeconds % 60);
};

/**
 * @param totalSeconds
 * @returns {string}
 */
const formatFinishTimeReadout = (totalSeconds) => {
  if (totalSeconds < 1) {
    return '';
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const paddedMinutes = String(minutes).padStart(2, '0');
  const paddedSeconds = String(seconds).padStart(2, '0');

  return hours > 0
    ? `${hours}:${paddedMinutes}:${paddedSeconds}`
    : `${minutes}:${paddedSeconds}`;
};

/**
 * @param totalSeconds
 * @param unitLabel
 * @returns {string}
 */
const formatPaceReadout = (totalSeconds, unitLabel) => {
  if (totalSeconds < 1) {
    return '';
  }

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${String(seconds).padStart(2, '0')} ${unitLabel}`;
};

/**
 * Proof events require a URL. Allowlisted events also receive a s/km cap and only
 * require the URL when the estimate is at or faster than that cap.
 * Pace is always derived from finish time and metres so min/mile entry cannot disagree with the server.
 * @param data
 */
const initializeResultsUrlRequirement = (data) => {
  const $group = data.elements.$element.find(selectors.resultUrl);
  const $input = $group.find(selectors.resultUrlInput);
  const $indicator = $group.find('[data-js-required-indicator]');
  const $units = data.elements.$element.find(selectors.durationUnit);
  const proofEnabled = Number.parseInt($group.data('resultsUrlProofEnabled'), 10) === 1;
  const maxSecondsPerKm = Number.parseInt($group.data('resultsUrlMaxSecondsPerKm'), 10);
  const distanceMeters = Number.parseInt($group.data('distanceMeters'), 10);
  const paceGated = Number.isFinite(maxSecondsPerKm) && maxSecondsPerKm > 0;

  if (!$group.length || !$input.length) {
    return;
  }

  const syncResultsUrlRequirement = () => {
    let isRequired = proofEnabled;
    if (proofEnabled && paceGated) {
      const finishTimeSeconds = finishTimeSecondsFromFields($units);
      const paceSecondsPerKm = (Number.isFinite(distanceMeters) && distanceMeters >= 1 && finishTimeSeconds >= 1)
        ? Math.round((finishTimeSeconds * 1000) / distanceMeters)
        : null;
      isRequired = paceSecondsPerKm !== null && paceSecondsPerKm <= maxSecondsPerKm;
    }

    $input.prop('required', isRequired);
    $indicator.toggleClass('hidden', !isRequired);
  };

  $units
    .off(`input.${pluginName}.resultUrl`)
    .on(`input.${pluginName}.resultUrl`, syncResultsUrlRequirement);

  data.syncResultsUrlRequirement = syncResultsUrlRequirement;
  syncResultsUrlRequirement();
};

/**
 * Treat the three optional unit controls as one required duration.
 * This permits values such as 45 minutes without forcing an explicit zero into the hours field.
 * @param data
 */
const initializeDurationValidation = (data) => {
  const $duration = data.elements.$element.find(selectors.duration);
  const $units = $duration.find(selectors.durationUnit);
  const $feedback = $duration.find(selectors.durationFeedback);
  const firstUnit = $units.get(0);

  if (!firstUnit) {
    return;
  }

  const isPaceMode = () => data.elements.$element.find(selectors.entryMode).filter(':checked').val() === 'pace';

  const validateDuration = () => {
    if (isPaceMode()) {
      // Pace mode hides these inputs. Drop a value past 23:59:59 so the hidden hours max cannot block submit.
      if (finishTimeSecondsFromFields($units) > MAX_FINISH_TIME_SECONDS) {
        $units.val('');
      }
      firstUnit.setCustomValidity('');
      $(firstUnit).attr('aria-invalid', 'false');
      $duration.removeClass('has-error');
      $feedback.addClass('hidden');
      return;
    }

    const totalSeconds = finishTimeSecondsFromFields($units);
    let message = '';
    if (totalSeconds < 1) {
      message = $duration.data('duration-error') || '';
    } else if (totalSeconds > MAX_FINISH_TIME_SECONDS) {
      // Hours max is 23. This still rejects a mix such as 23 hours and 60 minutes.
      message = $duration.data('duration-max-error') || '';
    }

    firstUnit.setCustomValidity(message);

    if (message === '') {
      $(firstUnit).attr('aria-invalid', 'false');
      $duration.removeClass('has-error');
      $feedback.addClass('hidden');
    } else {
      $feedback.text(message);
      if ($feedback.hasClass('hidden')) {
        $(firstUnit).removeAttr('aria-invalid');
      }
    }
  };

  $units
    .off(`input.${pluginName}`)
    .on(`input.${pluginName}`, validateDuration);
  $(firstUnit)
    .off(`invalid.${pluginName}`)
    .on(`invalid.${pluginName}`, () => {
      if (isPaceMode()) {
        return;
      }
      $feedback.text(firstUnit.validationMessage);
      $(firstUnit).attr('aria-invalid', 'true');
      $duration.addClass('has-error');
      $feedback.removeClass('hidden');
    });

  data.validateDuration = validateDuration;
  validateDuration();
};

/**
 * Keep finish time and pace in sync without posting pace.
 * @param data
 */
const initializePaceConversion = (data) => {
  const $conversion = data.elements.$element.find(selectors.conversion);
  const $duration = data.elements.$element.find(selectors.duration);
  const $durationUnits = data.elements.$element.find(selectors.durationUnit);
  const $durationCalculated = $conversion.find(selectors.durationCalculated);
  const $durationReadout = $durationCalculated.find(selectors.durationReadout);
  const $pace = data.elements.$element.find(selectors.pace);
  const $paceMinutes = $pace.find(selectors.paceMinutes);
  const $paceSeconds = $pace.find(selectors.paceSeconds);
  const $paceUnit = $pace.find(selectors.paceUnit);
  const $paceFeedback = $pace.find(selectors.paceFeedback);
  const $paceCalculated = $conversion.find(selectors.paceCalculated);
  const $paceReadout = $paceCalculated.find(selectors.paceReadout);
  const $entryMode = data.elements.$element.find(selectors.entryMode);
  const paceSecondsField = $paceSeconds.get(0);
  const distanceMeters = Number.parseInt($conversion.data('distanceMeters'), 10);
  const defaultPaceUnit = $conversion.data('defaultPaceUnit') || 'km';

  if (!$conversion.length || !paceSecondsField || !Number.isFinite(distanceMeters) || distanceMeters < 1) {
    return;
  }

  let isSyncing = false;

  const isPaceMode = () => $entryMode.filter(':checked').val() === 'pace';

  const selectedUnit = () => $paceUnit.val() || defaultPaceUnit;

  const unitsForDistance = (unit = selectedUnit()) => distanceMeters / metresPerUnit(unit);

  const unitLabel = (unit) => (
    unit === 'mi'
      ? ($conversion.data('unitLabelMi') || 'min/mile')
      : ($conversion.data('unitLabelKm') || 'min/km')
  );

  const showPaceError = (message) => {
    $pace.addClass('has-error');
    $paceFeedback.text(message).removeClass('hidden');
    $(paceSecondsField).attr('aria-invalid', 'true');
  };

  const clearPaceError = () => {
    $pace.removeClass('has-error');
    $paceFeedback.text('').addClass('hidden');
    $(paceSecondsField).attr('aria-invalid', 'false');
  };

  const applyPaceError = (message, showError) => {
    paceSecondsField.setCustomValidity(message);
    if (showError || $pace.hasClass('has-error')) {
      showPaceError(message);
    }
    return false;
  };

  const updateReadouts = () => {
    const finishTimeSeconds = finishTimeSecondsFromFields($durationUnits);
    const paceInputSeconds = paceSecondsFromFields($paceMinutes, $paceSeconds);
    const showDurationReadout = !isPaceMode() || paceInputSeconds >= 1;
    const durationReadoutText = showDurationReadout ? formatFinishTimeReadout(finishTimeSeconds) : '';
    $durationReadout.text(durationReadoutText);

    let paceReadoutText = '';
    if (finishTimeSeconds >= 1) {
      const paceSeconds = Math.round(finishTimeSeconds / unitsForDistance(defaultPaceUnit));
      paceReadoutText = formatPaceReadout(paceSeconds, unitLabel(defaultPaceUnit));
    }
    $paceReadout.text(paceReadoutText);

    // Hide calculated labels until there is a convertible value.
    $durationCalculated.toggleClass(classes.emptyReadout, durationReadoutText === '');
    $paceCalculated.toggleClass(classes.emptyReadout, paceReadoutText === '');

    if (typeof data.syncResultsUrlRequirement === 'function') {
      data.syncResultsUrlRequirement();
    }
  };

  const applyMode = () => {
    const paceMode = isPaceMode();
    $conversion
      .toggleClass(classes.conversionPace, paceMode)
      .toggleClass(classes.conversionTime, !paceMode);
    updateReadouts();
  };

  const validatePace = ({ showError = false } = {}) => {
    if (!isPaceMode()) {
      paceSecondsField.setCustomValidity('');
      clearPaceError();
      return true;
    }

    const secondsValue = $paceSeconds.val();
    const parsedSeconds = secondsValue === '' ? 0 : Number.parseInt(secondsValue, 10);
    if (secondsValue !== '' && (Number.isNaN(parsedSeconds) || parsedSeconds < 0 || parsedSeconds >= 60)) {
      return applyPaceError($pace.data('pace-seconds-error'), showError);
    }

    const paceSeconds = paceSecondsFromFields($paceMinutes, $paceSeconds);
    if (paceSeconds < 1) {
      return applyPaceError($pace.data('pace-required-error') || $pace.data('pace-error'), showError);
    }

    const convertedFinish = Math.round(paceSeconds * unitsForDistance());
    if (convertedFinish < 1) {
      return applyPaceError($pace.data('pace-error'), showError);
    }

    if (convertedFinish > MAX_FINISH_TIME_SECONDS) {
      // Pace mode hides the duration fields. Fail here so a hidden hours input over 23 cannot block submit unseen.
      const maxError = $duration.data('duration-max-error') || '';
      return applyPaceError(maxError, showError);
    }

    paceSecondsField.setCustomValidity('');
    clearPaceError();
    return true;
  };

  const validateDurationForMode = () => {
    if (typeof data.validateDuration === 'function') {
      data.validateDuration();
    }
  };

  const syncPaceFromFinishTime = () => {
    const finishTimeSeconds = finishTimeSecondsFromFields($durationUnits);
    if (finishTimeSeconds < 1) {
      if (!isSyncing) {
        $paceMinutes.val('');
        $paceSeconds.val('');
      }
      validatePace();
      updateReadouts();
      return;
    }

    const paceSeconds = Math.round(finishTimeSeconds / unitsForDistance());
    if (paceSeconds < 1) {
      updateReadouts();
      return;
    }

    isSyncing = true;
    writePaceFields(paceSeconds, $paceMinutes, $paceSeconds);
    isSyncing = false;
    validatePace();
    updateReadouts();
  };

  const syncFinishTimeFromPace = () => {
    const paceSeconds = paceSecondsFromFields($paceMinutes, $paceSeconds);
    if (paceSeconds < 1) {
      if (!isSyncing) {
        $durationUnits.val('');
      }
      validatePace({ showError: true });
      validateDurationForMode();
      updateReadouts();
      return;
    }

    if (!validatePace({ showError: true })) {
      updateReadouts();
      return;
    }

    const finishTimeSeconds = Math.round(paceSeconds * unitsForDistance());
    if (finishTimeSeconds < 1) {
      updateReadouts();
      return;
    }

    isSyncing = true;
    writeFinishTimeFields(finishTimeSeconds, $durationUnits);
    isSyncing = false;
    validateDurationForMode();
    updateReadouts();
  };

  $durationUnits
    .off(`input.${pluginName}.pace`)
    .on(`input.${pluginName}.pace`, () => {
      if (!isSyncing) {
        syncPaceFromFinishTime();
      }
    });

  $paceMinutes
    .add($paceSeconds)
    .off(`input.${pluginName}`)
    .on(`input.${pluginName}`, () => {
      if (!isSyncing) {
        syncFinishTimeFromPace();
      }
    });

  $paceUnit
    .off(`change.${pluginName}`)
    .on(`change.${pluginName}`, () => {
      if (isSyncing) {
        return;
      }
      if (paceSecondsFromFields($paceMinutes, $paceSeconds) < 1) {
        validatePace();
        updateReadouts();
        return;
      }
      syncFinishTimeFromPace();
    });

  $entryMode
    .off(`change.${pluginName}`)
    .on(`change.${pluginName}`, () => {
      applyMode();
      validatePace();
      validateDurationForMode();
    });

  paceSecondsField.addEventListener('invalid', () => {
    if (isPaceMode()) {
      showPaceError(paceSecondsField.validationMessage);
    }
  });

  applyMode();
  validatePace();
  validateDurationForMode();
};

/**
 * Inject the feature HTML. Combined checkout passes markup from GET /features;
 * kiosk initializeContent calls this without HTML so existing fields stay in place.
 * @param data
 * @param html
 */
const setup = (data, html) => {
  if (html !== undefined) {
    data.elements.$element.html(html);
  }

  initializeDurationValidation(data);
  initializeResultsUrlRequirement(data);
  initializePaceConversion(data);
  data.setup = true;

  triggerHook(data, 'setup');
};

/**
 * Cancel any active requests being made
 * @param data
 */
const cancelActiveRequests = () => {};

const methods = {
  toggleVisibility,
  setup,
  initializeContent: setup,
  clear,
  cancelActiveRequests,
};

const getMethods = {
  serialize: (data) => {
    if (!data.setup) {
      return '';
    }

    return serialize(data);
  },
};

/**
 * Call one of our custom plugin methods on each given jQuery object
 * @param $element
 * @param method
 * @param methodParameters
 * @returns {jQuery}
 */
const callPluginMethod = ($element, method, ...methodParameters) => {
  const data = $element.data(pluginName);

  if (data) {
    methods[method](data, ...methodParameters);
  }

  return $element;
};

/**
 * Fire up
 * @param $element
 * @param options
 */
const initialize = ($element, options) => {
  if ($element.data(pluginName)) {
    return;
  }

  const settings = $.extend(true, {}, defaults, options);
  const data = {
    settings,
    elements: {
      $element,
    },
    setup: false,
  };

  data.elements.$element.data(pluginName, data);
  triggerHook(data, 'initialized');
};

/**
 * Plugin
 * @param methodOrOptions
 * @param methodParameters
 * @returns {jQuery}
 */
const plugin = function plugin(methodOrOptions, ...methodParameters) {
  if (getMethods[methodOrOptions]) {
    const $element = $(this);
    const data = $element.data(pluginName);

    if (!data) {
      return false;
    }

    return getMethods[methodOrOptions](data, ...methodParameters);
  }

  if (methods[methodOrOptions]) {
    return this
      .each((index, element) => callPluginMethod($(element), methodOrOptions, ...methodParameters));
  }

  if (typeof methodOrOptions === 'object' || !methodOrOptions) {
    return this.each((index, element) => initialize($(element), methodOrOptions));
  }

  return $.error(`Method ${methodOrOptions} does not exist in ${pluginName}`);
};

plugin.methods = methods;
plugin.getMethods = getMethods;

$(() => {
  if (pluginName in $.fn) {
    return;
  }

  $.extend($.fn, {
    [pluginName]: plugin,
  });
});
